import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ChevronDown, ChevronUp, FileText, AlertTriangle,
  MapPin, Clock, Shield, BookOpen, Compass, Scale, Check,
} from 'lucide-react';

const HERKUNFT_CONFIG = (t) => ({
  aus_nutzerangaben: { label: t('fa.herkunftNutzer'), cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  naheliegende_ableitung: { label: t('fa.herkunftAbleitung'), cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  regional_zu_pruefen: { label: t('fa.herkunftRegional'), cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  fachlich_gesondert_pruefen: { label: t('fa.herkunftFachlich'), cls: 'bg-orange-50 text-orange-700 border-orange-200' },
});

const PERSPEKTIVE_ICONS = {
  foerderung: BookOpen,
  oekologie: Shield,
  oekonomie: Scale,
  betriebswirtschaft: Scale,
  naturschutz: Shield,
  gesetze: FileText,
};

const PERSPEKTIVE_LABELS = (t) => ({
  foerderung: t('fa.perspFoerderung'),
  oekologie: t('fa.perspOekologie'),
  oekonomie: t('fa.perspOekonomie'),
  betriebswirtschaft: t('fa.perspBetriebswirtschaft'),
  naturschutz: t('fa.perspNaturschutz'),
  gesetze: t('fa.perspGesetze'),
});

const STOLP_KAT_LABELS = (t) => ({
  betriebswirtschaftlich: t('fa.stolpBetriebswirtschaftlich'),
  oekologisch: t('fa.stolpOekologisch'),
  organisatorisch: t('fa.stolpOrganisatorisch'),
  gesetzlich: t('fa.stolpGesetzlich'),
  foerderbezogen: t('fa.stolpFoerderbezogen'),
  naturschutz: t('fa.stolpNaturschutz'),
});

const PHASE_CONFIG = (t) => ({
  jetzt_klaeren: { label: t('fa.phaseJetzt'), cls: 'border-red-200 bg-red-50/50', icon: Clock },
  dann_pruefen: { label: t('fa.phaseDann'), cls: 'border-amber-200 bg-amber-50/50', icon: FileText },
  danach_entscheiden: { label: t('fa.phaseDanach'), cls: 'border-emerald-200 bg-emerald-50/50', icon: Check },
});

const REGIONAL_KAT_LABELS = (t) => ({
  foerderung: t('fa.regFoerderung'),
  landesrecht: t('fa.regLandesrecht'),
  naturschutz: t('fa.regNaturschutz'),
  verwaltung: t('fa.regVerwaltung'),
});

function HerkunftBadge({ herkunft, t }) {
  const cfg = HERKUNFT_CONFIG(t)[herkunft] || { label: herkunft, cls: 'bg-gray-50 text-gray-600 border-gray-200' };
  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${cfg.cls}`}>
      {cfg.label}
    </Badge>
  );
}

export function LagebeurteilungView({ lb, t }) {
  const [showFachlich, setShowFachlich] = useState(false);
  const [showRegional, setShowRegional] = useState(false);
  const perspLabels = PERSPEKTIVE_LABELS(t);
  const stolpLabels = STOLP_KAT_LABELS(t);
  const phaseConfig = PHASE_CONFIG(t);
  const regLabels = REGIONAL_KAT_LABELS(t);

  if (!lb) return null;

  return (
    <div className="space-y-6" data-testid="lagebeurteilung-view">
      {/* Kurzfazit */}
      {lb.kurzfazit && (
        <div className="p-4 rounded-lg border border-primary/20 bg-primary/[0.02]" data-testid="lb-kurzfazit">
          <p className="text-xs font-medium text-primary uppercase tracking-wide mb-1">{t('fa.lbKurzfazit')}</p>
          {/* Handle both string and object with saetze array */}
          {typeof lb.kurzfazit === 'string' ? (
            <p className="text-sm text-foreground leading-relaxed font-medium">{lb.kurzfazit}</p>
          ) : lb.kurzfazit.saetze ? (
            <div className="space-y-1">
              {lb.kurzfazit.saetze.map((satz, i) => (
                <p key={i} className="text-sm text-foreground leading-relaxed font-medium">{satz}</p>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* Vorentscheidung_kontext */}
      {lb.vorentscheidung_kontext && (
        <div data-testid="lb-vorentscheidung-kontext">
          <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
            <Compass className="h-4 w-4 text-primary" />
            {t('fa.lbVorentscheidungKontext')}
          </h3>
          {/* Handle both string and object with text property */}
          {typeof lb.vorentscheidung_kontext === 'string' ? (
            <p className="text-sm text-foreground leading-relaxed">{lb.vorentscheidung_kontext}</p>
          ) : lb.vorentscheidung_kontext.text ? (
            <>
              <p className="text-sm text-foreground leading-relaxed">{lb.vorentscheidung_kontext.text}</p>
              {lb.vorentscheidung_kontext.herkunft && (
                <HerkunftBadge herkunft={lb.vorentscheidung_kontext.herkunft} t={t} />
              )}
            </>
          ) : null}
        </div>
      )}

      {/* Was vor Entscheidung fehlt */}
      {lb.was_vor_entscheidung_fehlt?.length > 0 && (
        <div data-testid="lb-was-fehlt">
          <h3 className="text-sm font-semibold text-foreground mb-2">{t('fa.lbWasFehlt')}</h3>
          <div className="space-y-2">
            {lb.was_vor_entscheidung_fehlt.map((item, i) => {
              const pCfg = phaseConfig[item.phase] || phaseConfig.jetzt_klaeren;
              const PhaseIcon = pCfg.icon;
              return (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${pCfg.cls}`}>
                  <PhaseIcon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-medium text-foreground">{item.punkt}</span>
                      <Badge variant="outline" className="text-[9px] px-1 py-0">{pCfg.label}</Badge>
                      <HerkunftBadge herkunft={item.herkunft} t={t} />
                    </div>
                    <p className="text-xs text-muted-foreground">{item.loesungshinweis}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stolpersteine */}
      {lb.stolpersteine?.length > 0 && (
        <div data-testid="lb-stolpersteine">
          <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {t('fa.lbStolpersteineRisiken')}
          </h3>
          <div className="space-y-2">
            {lb.stolpersteine.map((s, i) => (
              <div key={i} className="p-3 rounded-lg border border-warm-200 bg-warm-50/50">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-warm-300 text-warm-700">
                    {stolpLabels[s.kategorie] || s.kategorie}
                  </Badge>
                  {s.relevanz === 'hoch' && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-red-200 text-red-600 bg-red-50">
                      {t('fa.relevanzHoch')}
                    </Badge>
                  )}
                  <HerkunftBadge herkunft={s.herkunft} t={t} />
                </div>
                <p className="text-sm text-foreground">{s.text}</p>
                {s.konsequenz && <p className="text-xs text-muted-foreground mt-1">{s.konsequenz}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fachliche Einordnung (aufklappbar) */}
      {lb.fachliche_einordnung && (
        <div data-testid="lb-fachliche-einordnung">
          <button
            onClick={() => setShowFachlich(!showFachlich)}
            className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
          >
            {showFachlich ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            <FileText className="h-4 w-4 text-primary" />
            {t('fa.lbFachlicheEinordnung')}
          </button>
          {showFachlich && (
            <div className="mt-3 space-y-3">
              {lb.fachliche_einordnung.perspektiven?.map((p, i) => {
                const PerspIcon = PERSPEKTIVE_ICONS[p.perspektive] || FileText;
                return (
                  <div key={i} className="p-3 rounded-lg border border-warm-200">
                    <div className="flex items-center gap-2 mb-1">
                      <PerspIcon className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-medium text-foreground">{perspLabels[p.perspektive] || p.perspektive}</span>
                    </div>
                    <p className="text-sm text-foreground">{p.einordnung}</p>
                    <HerkunftBadge herkunft={p.herkunft} t={t} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Regionale Einordnung (aufklappbar) */}
      {lb.regionale_einordnung?.hinweise?.length > 0 && (
        <div data-testid="lb-regionale-einordnung">
          <button
            onClick={() => setShowRegional(!showRegional)}
            className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
          >
            {showRegional ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            <MapPin className="h-4 w-4 text-primary" />
            {t('fa.lbRegionaleEinordnung')} {lb.regionale_einordnung.bundesland}
          </button>
          {showRegional && (
            <div className="mt-3 space-y-2">
              {lb.regionale_einordnung.hinweise.map((h, i) => (
                <div key={i} className="p-3 rounded-lg border border-warm-200 bg-warm-50/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/20 text-primary/70">
                      {regLabels[h.kategorie] || h.kategorie}
                    </Badge>
                  </div>
                  <p className="text-sm text-foreground">{h.text}</p>
                  {h.quelle && <p className="text-xs text-muted-foreground mt-1">{h.quelle}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Was jetzt sinnvoll ist */}
      {lb.was_jetzt_sinnvoll?.length > 0 && (
        <div data-testid="lb-was-sinnvoll">
          <h3 className="text-sm font-semibold text-foreground mb-2">{t('fa.lbWasSinnvoll')}</h3>
          <div className="space-y-2">
            {lb.was_jetzt_sinnvoll.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-primary/10 bg-primary/[0.02]">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-xs font-bold text-primary">
                  {i + 1}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{item.schritt}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.erklaerung}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <p className="text-[11px] text-muted-foreground/70 leading-relaxed border-t border-warm-200 pt-4" data-testid="lb-disclaimer">
        {t('fa.lbDisclaimer')}
      </p>
    </div>
  );
}
